/* ============================================================
   src/shared/useLeadList.ts
   ----------------------------------------------------------------
   The saved lead list as React state: this person's own Zoho
   appointments, today → today+7, kept on the device (leadList.ts).
   Shared by the desktop/web picker and, later, mobile.

   - Downloaded on launch and whenever the connection comes back,
     and on demand via refresh() ("Refresh from Zoho").
   - A failed download keeps whatever is stored — exactly the
     no-signal case — and sets refreshFailed.
   - A different login re-reads (another login's list is wiped by
     loadLeadList).
   - Only the `leads` download ever writes the list. The picker's
     everyone's-appointments day view must never call save.
   ============================================================ */

import { useCallback, useEffect, useRef, useState } from "react";
import { nyTodayDate } from "./zohoImport";
import {
  downloadLeadList,
  loadLeadList,
  saveLeadList,
  type OfflineLead,
  type StoredLeadList,
} from "./leadList";

export type LeadListState = {
  /** The stored list, past days already dropped; null if none on this device. */
  leadList: StoredLeadList | null;
  /** A download is in flight. */
  refreshing: boolean;
  /** The last download failed (the stored list was kept). */
  refreshFailed: boolean;
  /** Download now and save it. No-op without a login or Zoho access. */
  refresh: () => Promise<void>;
  /** Re-read storage — drops days that ended while the app stayed open. */
  reload: () => void;
};

export function useLeadList(opts: {
  userId: string | null;
  /** Zoho access (the allow-list) — without it nothing is downloaded. */
  canDownload: boolean;
  isOnline: boolean;
}): LeadListState {
  const { userId, canDownload, isOnline } = opts;

  const [leadList, setLeadList] = useState<StoredLeadList | null>(() =>
    loadLeadList(userId, nyTodayDate())
  );
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const downloadSeq = useRef(0);

  // A different login in the same window (or none) → re-read for that login.
  useEffect(() => {
    setLeadList(loadLeadList(userId, nyTodayDate()));
  }, [userId]);

  const refresh = useCallback(async () => {
    if (!userId || !canDownload) return;
    const seq = ++downloadSeq.current;
    setRefreshing(true);
    const res = await downloadLeadList();
    if (seq !== downloadSeq.current) return; // a newer download won
    setRefreshing(false);
    if (!res.ok) {
      setRefreshFailed(true);
      return;
    }
    setRefreshFailed(false);
    setLeadList(saveLeadList(userId, res.leads as OfflineLead[], nyTodayDate()));
  }, [userId, canDownload]);

  // On launch and each time the connection comes back.
  useEffect(() => {
    if (isOnline) void refresh();
  }, [isOnline, refresh]);

  const reload = useCallback(() => {
    setLeadList(loadLeadList(userId, nyTodayDate()));
  }, [userId]);

  return { leadList, refreshing, refreshFailed, refresh, reload };
}
